/**
 * Hold the host page still while the panel is open.
 *
 * `body { overflow: hidden }` is enough on a desktop. iOS Safari ignores it
 * for touch: the page under the panel kept scrolling, and when the keyboard
 * came up Safari scrolled the document to "show" the focused field. A
 * position:fixed panel is laid out against the layout viewport, so once the
 * document had moved the panel was DRAWN in one place and HIT-TESTED in
 * another — taps on the booking steps landed on the wrong rows (Tim,
 * 2026-10-08, iPhone). Pinning the body at its scroll offset is the one
 * approach iOS honours; the offset is put back on unlock.
 */

let locked = false;
let savedScrollY = 0;
let saved: Record<string, string> = {};

const BODY_PROPS = ['position', 'top', 'left', 'right', 'width', 'overflow'] as const;

function touchScreen(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
  } catch { return false; }
}

export function lockPage(): void {
  if (locked || typeof document === 'undefined') return;
  locked = true;
  const body = document.body;
  saved = {};
  for (const p of BODY_PROPS) saved[p] = body.style.getPropertyValue(p);
  saved.htmlOverflow = document.documentElement.style.overflow;
  body.style.overflow = 'hidden';
  if (touchScreen()) {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    body.style.position = 'fixed';
    body.style.top = `-${savedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
  }
}

export function unlockPage(): void {
  if (!locked || typeof document === 'undefined') return;
  locked = false;
  const body = document.body;
  const pinned = body.style.position === 'fixed';
  for (const p of BODY_PROPS) {
    if (saved[p]) body.style.setProperty(p, saved[p]); else body.style.removeProperty(p);
  }
  document.documentElement.style.overflow = saved.htmlOverflow || '';
  if (pinned) window.scrollTo(0, savedScrollY);
}

export function isPageLocked(): boolean { return locked; }
